import React, {useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import Avatar from "../../system/components/avatar";
import Icon from "../../system/components/icon";
import Span from "../../system/components/span";
import TwemojiText, {isEmojiOnly} from "../../system/components/twemoji-text";
import {useTranslate} from "../../system/useTranslate";
import {CHAT_REFERENCE_CONFIG, tokenPart, valueList} from "./chat-composer";
import ChatMessageReactions from "./chat-message-reactions";
import ChatMessageAttachment from "./chat-message-attachment";
import UpdateChatCard, {isUpdateCard} from "./update-chat-card";
import {renderFormattedText} from "./text-formatting";

export function MessageContent({content, mentions, onContextMenu, standaloneEmoji = false}) {
    const handleContextMenu = (event) => {
        event.preventDefault();
        const selection = window.getSelection?.();
        const anchorInside = selection?.anchorNode && event.currentTarget.contains(selection.anchorNode);
        const focusInside = selection?.focusNode && event.currentTarget.contains(selection.focusNode);
        const selectedText = anchorInside && focusInside && !selection.isCollapsed
            ? selection.toString().trim()
            : "";
        onContextMenu?.(event, selectedText || String(content || "").trim());
    };

    const mentionTokens = valueList(mentions)
        .map((mention) => `@${mention?.token || tokenPart(mention?.fullname || mention?.username)}`)
        .filter((token) => token !== "@");

    if (standaloneEmoji) {
        return (
            <TwemojiText
                className="w-full white-space-pre-wrap text-6xl line-height-1"
                imageClassName="inline-block vertical-align-middle w-4rem h-4rem"
                onContextMenu={handleContextMenu}
            >
                {content}
            </TwemojiText>
        );
    }

    // **bold**, _italic_, ~~strike~~ and «- » bullets written with the composer's toolbar (text-formatting.js)
    if (!mentionTokens.length) {
        return (
            <span onContextMenu={handleContextMenu} className="w-full white-space-pre-wrap text-justify text-xs">
                {renderFormattedText(content)}
            </span>
        );
    }

    const escaped = mentionTokens
        .sort((left, right) => right.length - left.length)
        .map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const pattern = new RegExp(`(${escaped.join("|")})`, "g");
    const tokenSet = new Set(mentionTokens);

    return (
        <span onContextMenu={handleContextMenu} className="w-full text-justify text-xs white-space-pre-wrap">
            {String(content || "").split(pattern).map((part, index) => tokenSet.has(part) ? (
                <strong key={`${part}-${index}`} className="text-primary-700 bg-primary-50 border-round px-1">
                    <TwemojiText>{part}</TwemojiText>
                </strong>
            ) : <React.Fragment key={`${part}-${index}`}>{renderFormattedText(part, `p${index}`)}</React.Fragment>)}
        </span>
    );
}

function referenceDetails(reference, translate) {
    const status = reference?.status ? translate(reference.status) : null;
    switch (reference?.type) {
        case "task":
            return [
                ["وضعیت", status],
                ["مسئول", reference.assignee],
                ["پروژه", reference.project],
                ["date", reference.dueDate],
            ];
        case "project":
            return [
                ["وضعیت", status],
                ["مالک", reference.owner],
                ["تیم", reference.team],
                ["date", reference.dueDate],
            ];
        case "deliverable":
            return [
                ["وضعیت", status],
                ["مسئول", reference.assignee],
                ["پروژه", reference.project],
                ["date", reference.dueDate],
            ];
        case "goal":
            return [
                ["وضعیت", status],
                ["مالک", reference.owner],
                ["تیم", reference.team],
                ["date", reference.dueDate],
            ];
        case "team":
            return [
                ["مالک", reference.owner],
                ["اعضا", `${Number(reference.memberCount || 0)} عضو`],
            ];
        case "customer":
            return [
                ["وضعیت", status],
                ["شرکت", reference.company],
                ["مسئول", reference.agent],
            ];
        default:
            return [];
    }
}

export function LinkedEntityCard({reference, onOpen}) {
    // goal / project updates sent from their modal have their own fixed template (update-chat-card.js)
    if (isUpdateCard(reference)) return <UpdateChatCard reference={reference}/>;
    return <ReferenceCard reference={reference} onOpen={onOpen}/>;
}

function ReferenceCard({reference, onOpen}) {
    const translate = useTranslate();
    const config = CHAT_REFERENCE_CONFIG[reference?.type] || CHAT_REFERENCE_CONFIG.task;
    const details = referenceDetails(reference, translate).filter(([, value]) => value);
    const canOpen = Boolean(onOpen || reference?.link);
    const openReference = () => {
        onOpen?.(reference);
    };

    return (
        <div
            role={canOpen ? "button" : undefined}
            tabIndex={canOpen ? 0 : undefined}
            onClick={openReference}
            onKeyDown={(event) => {
                if (canOpen && ["Enter", " "].includes(event.key)) {
                    event.preventDefault();
                    openReference();
                }
            }}
            className={`w-full surface-card border-1 border-200 border-round p-2 flex flex-column gap-2 ${canOpen ? "cursor-pointer hover:surface-50" : ""}`}
        >
            <div className="flex align-items-center gap-2">
                <span className="w-2rem h-2rem border-circle surface-100 flex align-items-center justify-content-center flex-shrink-0">
                    <Icon icon={config.icon} className={`${config.color} text-lg`}/>
                </span>
                <div className="flex flex-column min-w-0 flex-1">
                    <small className={`text-xs ${config.color}`}>{config.label}</small>
                    <strong className="text-xs overflow-hidden text-overflow-ellipsis white-space-nowrap">
                        {reference?.title || "بدون عنوان"}
                    </strong>
                </div>
                {canOpen && <Icon icon="mi mi:chevron_left" className="text-sm text-400"/>}
            </div>

            {reference?.description && (
                <span className="text-xs text-600 line-height-3">{reference.description}</span>
            )}

            {!!details.length && (
                <div className="flex flex-wrap gap-2 text-xs text-500">
                    {details.map(([label, value], index) => label === "date" ? (
                        <span key={`date-${index}`} className="flex align-items-center gap-1">
                            <Icon icon="mi mi:event" className="text-sm"/>
                            <Span type="PERSIAN_DATE" format="YYYY/MM/DD" spanClassName="text-xs text-500">{value}</Span>
                        </span>
                    ) : (
                        <span key={`${label}-${index}`} className="surface-100 border-round px-2 py-1">
                            {label}: {value}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function ChatMessage({
    item,
    seen = false,
    showSender = false,
    currentMembership,
    reactionLoading = false,
    onReact,
    onEdit,
    onDelete,
    onReply,
    onOpenReference,
    onTextContextMenu,
    onAttachmentError,
}) {
    const [hover, setHover] = useState(false);
    const {workspaceMembershipData, workspaceId} = useSelector((state) => state.app);
    const activeWorkspaceId = workspaceMembershipData?.workspace?.documentId || workspaceId;
    const messageUser = item?.user?.member || {id: 0};
    const ownMessage = String(item?.user?.documentId || item?.user?.id)
        === String(workspaceMembershipData?.documentId || workspaceMembershipData?.id);
    const standaloneEmoji = isEmojiOnly(item?.messageContent)
        && !item?.parent
        && !valueList(item?.attachment).length
        && !valueList(item?.linkedEntity).length;
    return (
        <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            className={`flex w-full p-2 ${ownMessage ? "justify-content-start" : "justify-content-end"}`}
        >
            <div
                style={{minHeight: "8rem"}}
                className={`flex align-items-end h-full gap-1 ${ownMessage ? "flex-row" : "flex-row-reverse"}`}
            >
                {showSender && (
                    <Avatar size="normal" memberData={messageUser} className="text-2xs font-bold flex-shrink-0"/>
                )}
                <div
                    className={`${standaloneEmoji ? "p-0" : `p-2 ${ownMessage ? "bg-primary-100" : "surface-200"}`} overflow-hidden max-w-30rem border-round ${ownMessage ? "align-items-end" : "align-items-start"} flex flex-column justify-content-between w-fit ${standaloneEmoji ? "gap-0" : "gap-3"}`}
                    onContextMenu={(event) => onTextContextMenu?.(
                        event,
                        item,
                        String(item?.messageContent || "").trim()
                    )}
                >
                    {item?.parent && (
                        <div
                            style={{borderRight: "solid 5px"}}
                            className={`p-2 border-round flex align-items-center gap-2 justify-content-start w-full ${ownMessage ? "bg-primary-200 border-primary" : "surface-300 border-400"}`}
                        >
                            {item.parent?.attachment?.[0] && (
                                <ChatMessageAttachment
                                    compact
                                    file={item.parent.attachment[0]}
                                    workspaceId={activeWorkspaceId}
                                    onError={onAttachmentError}
                                />
                            )}
                            <span className="text-2xs font-italic text-black-alpha-20">
                                {item.parent.messageContent
                                    || item.parent?.attachment?.[0]?.name
                                    || valueList(item.parent?.linkedEntity)?.[0]?.title}
                            </span>
                        </div>
                    )}

                    {valueList(item?.linkedEntity).map((reference) => (
                        <LinkedEntityCard
                            key={`${reference.type}-${reference.documentId || reference.id}`}
                            reference={reference}
                            onOpen={onOpenReference}
                        />
                    ))}

                    {valueList(item?.attachment).map((file) => (
                        <ChatMessageAttachment
                            key={file?.documentId || file?.id}
                            file={file}
                            workspaceId={activeWorkspaceId}
                            ownMessage={ownMessage}
                            onError={onAttachmentError}
                        />
                    ))}

                    {item?.messageContent && (
                        <MessageContent
                            content={item.messageContent}
                            mentions={item.mentions}
                            standaloneEmoji={standaloneEmoji}
                            onContextMenu={(event, selectedText) => onTextContextMenu?.(event, item, selectedText)}
                        />
                    )}

                    <ChatMessageReactions
                        message={item}
                        currentMembership={currentMembership}
                        loading={reactionLoading}
                        onReact={onReact}
                    />

                    {!standaloneEmoji && <div className="flex align-items-center gap-2">
                        {ownMessage && (
                            <Icon
                                icon={seen ? "mi mi:done_all" : "mi mi:check"}
                                className={`${seen ? "text-blue-500" : "text-400"} text-lg`}
                            />
                        )}
                        <Span spanClassName="text-2xs text-400 ltr" type="PERSIAN_DATE" format="HH:mm">
                            {item?.sentAt || item?.createdAt}
                        </Span>
                    </div>}
                </div>

                <div className="flex flex-column w-2rem gap-1">
                    {hover && (
                        <>
                            {ownMessage && item?.messageContent && (
                                <Button
                                    className="p-button-rounded p-button-secondary p-button-text surface-100 active:surface-200 p-button-sm shadow-none border-none w-2rem h-2rem p-0"
                                    icon={<Icon icon="mi mi:edit" className="text-sm"/>}
                                    aria-label="ویرایش پیام"
                                    onClick={() => onEdit?.(item)}
                                />
                            )}
                            {ownMessage && (
                                <Button
                                    className="p-button-rounded p-button-secondary p-button-text surface-100 active:surface-200 p-button-sm shadow-none border-none w-2rem h-2rem p-0"
                                    icon={<Icon icon="mi mi:delete" className="text-sm"/>}
                                    aria-label="حذف پیام"
                                    onClick={() => onDelete?.(item)}
                                />
                            )}
                            <Button
                                className="p-button-rounded p-button-secondary p-button-text surface-100 active:surface-200 p-button-sm shadow-none border-none w-2rem h-2rem p-0"
                                icon={<Icon icon="mi mi:reply" className="text-sm"/>}
                                aria-label="پاسخ به پیام"
                                onClick={() => onReply?.(item)}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ChatMessage;
